import { Form, type FormInstance, Spin } from 'antd'
import { useLocalStorageState } from 'ahooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans } from '@lingui/react/macro'

import TagSelect from '../TagSelect'
import { taskTypeMap } from '../../_blocks/GeneratorForm/_data'
import { useMusicFormStore } from '../../_store/useMusicFormStore'

import type { MusicGeneratorFormValuesNew } from '../../_types/form'
import type { TagSelectProps } from '../TagSelect'

import { api } from '@/utils/api'
import { CssIcon } from '@/components/CssIcon'


import { cls } from 'tagged-classnames-free';
export interface IdeasRenderParams {
  displayIdeas: string[]
  isFetching: boolean
  hasDisplayableCache: boolean
  handleRefresh: () => void
  onSelect: (value: string) => void
  TagSelect: React.FC<TagSelectProps>
}

interface IdeasProps<T extends MusicGeneratorFormValuesNew> {
  form: FormInstance<T>
  className?: string
  tagClassName?: string
  ulClassName?: string
  renderFn?: (params: IdeasRenderParams) => React.ReactNode
}
function Ideas<T extends MusicGeneratorFormValuesNew>(props: IdeasProps<T>) {
  const { form, className, tagClassName, renderFn } = props

  const taskType = Form.useWatch(['taskType'], form)
  const setFormValues = useMusicFormStore((s) => s.setFormValues)

  const musicType = useMemo(() => {
    return taskType === taskTypeMap.Instrumental ? 'Instrumental' : 'Song'
  }, [taskType])

  const [ideaMap, setIdeaMap] = useLocalStorageState<{
    ideas: string[]
    displayedIndexes: number[] // 跟踪已展示的数据索引
  }>('form-ideaMap', {
    defaultValue: {
      ideas: [],
      displayedIndexes: [],
    },
    listenStorageChange: true,
  })

  const [hasInitialLoaded, setHasInitialLoaded] = useState(false)

  useEffect(() => {
    const { ideas = [] } = ideaMap || {}
    if (ideas.length > 0 && !hasInitialLoaded) {
      setHasInitialLoaded(true)
    }
  }, [ideaMap, hasInitialLoaded])

  // 检查是否有可展示的缓存数据
  const hasDisplayableCache = useMemo(() => {
    const { ideas = [], displayedIndexes = [] } = ideaMap || {}
    if (ideas.length === 0) return false

    // 如果还没有展示过任何数据，说明有可展示的缓存
    if (displayedIndexes.length === 0) return true

    // 或者如果有未展示过的数据，也算有可展示的缓存
    const allIndexes = Array.from({ length: ideas.length }, (_, i) => i)
    const unDisplayedIndexes = allIndexes.filter(
      (index) => !displayedIndexes.includes(index),
    )
    return unDisplayedIndexes.length > 0 || displayedIndexes.length >= 3
  }, [ideaMap])

  const { isFetching, refetch } = api.preset.ideas.useQuery(
    {
      musicType,
      limit: 10, // 拉取10条数据
    },
    {
      enabled: !ideaMap || !ideaMap?.ideas || ideaMap?.ideas?.length === 0,
      onSuccess(data) {
        if (!hasInitialLoaded) {
          // 首次加载，设置初始显示
          setIdeaMap({
            ideas: data,
            displayedIndexes: [0, 1, 2], // 初始显示前3条
          })
          setHasInitialLoaded(true)
        } else {
          // 后续请求，重置显示状态
          setIdeaMap({
            ideas: data,
            displayedIndexes: [0, 1, 2],
          })
        }
      },
    },
  )

  // 刷新函数 - 优先使用缓存中未展示的数据
  const handleRefresh = useCallback((): void => {
    const { ideas = [], displayedIndexes = [] } = ideaMap || {}

    if (ideas.length === 0) {
      // 没有缓存，请求接口
      refetch()
      return
    }

    // 如果还没有显示记录，设置前3条并标记为已显示
    if (displayedIndexes.length === 0) {
      setIdeaMap({
        ideas,
        displayedIndexes: [0, 1, 2],
      })
      return
    }

    // 找出未展示过的数据索引
    const allIndexes = Array.from({ length: ideas.length }, (_, i) => i)
    const unDisplayedIndexes = allIndexes.filter(
      (index) => !displayedIndexes.includes(index),
    )

    if (unDisplayedIndexes.length >= 3) {
      // 还有足够的未展示数据，随机选择3条
      const shuffled = [...unDisplayedIndexes].sort(() => Math.random() - 0.5)
      const newDisplayedIndexes = shuffled.slice(0, 3)

      setIdeaMap({
        ideas,
        displayedIndexes: [...displayedIndexes, ...newDisplayedIndexes],
      })
    } else if (unDisplayedIndexes.length > 0) {
      // 剩余未展示数据不足3条，全部显示
      setIdeaMap({
        ideas,
        displayedIndexes: [...displayedIndexes, ...unDisplayedIndexes],
      })
    } else {
      // 所有缓存数据都已展示过，重新请求接口
      refetch().then((result) => {
        if (result.data) {
          // 手动设置新数据和重置显示索引
          setIdeaMap({
            ideas: result.data,
            displayedIndexes: [0, 1, 2],
          })
        }
      })
    }
  }, [ideaMap, refetch, setIdeaMap])

  // 选择 tag 的内部事件，暴露给外部自定义 UI 使用
  const handleSelect = useCallback(
    (value: string) => {
      form.setFieldsValue({
        prompt: value,
      } as any)
      setFormValues({ prompt: value })
    },
    [form, setFormValues],
  )

  // 当前要显示的3条数据
  const displayIdeas = useMemo(() => {
    const { ideas = [], displayedIndexes = [] } = ideaMap || {}
    if (ideas.length === 0) return []

    // 如果还没有任何显示记录，显示前3条
    if (displayedIndexes.length === 0) {
      return ideas.slice(0, 3)
    }

    // 获取当前应该显示的索引（最后3个）
    const currentDisplayIndexes = displayedIndexes.slice(-3)
    return currentDisplayIndexes.map((index) => ideas[index]).filter(Boolean)
  }, [ideaMap])

  // 外部自定义 UI 模式
  // 便于拓展，ui由外部去决定，很多情况下只是ui的差别，事件处理并无差别
  if (typeof renderFn === 'function') {
    return (
      <div className={cls`${className}`}>
        {renderFn({
          displayIdeas,
          isFetching,
          hasDisplayableCache,
          handleRefresh,
          onSelect: handleSelect,
          TagSelect: TagSelect as React.FC<TagSelectProps>,
        })}
      </div>
    )
  }

  // default UI
  return (
    <div className={cls`${className}`}>
      <div className={cls`mb-2 flex items-center justify-between`}>
        <div className={cls`text-lg font-semibold`}>
          <Trans>Ideas</Trans>
        </div>
        <CssIcon
          className={cls`i-cus--tad-change text-f-text-quaternary hover:text-f-text size-4 cursor-pointer`}
          onClick={() => {
            handleRefresh()
          }}
        />
      </div>
      <Spin spinning={isFetching && !hasDisplayableCache}>
        <TagSelect
          tags={displayIdeas}
          className={cls`${tagClassName}`}
          onChange={handleSelect}
        />
      </Spin>
    </div>
  )
}

export default Ideas
